import React, { useEffect, useState, useRef } from 'react';
import { useStateValue } from "../GlobalState/StateProvider";
import { useNavigate } from 'react-router-dom';
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { TabView, TabPanel } from 'primereact/tabview';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { FilterMatchMode } from 'primereact/api';
import axios from "axios";
//import './Dashboard.css';

function MailOrder() {
    const navigate = useNavigate();
    const [{ user }, dispatch] = useStateValue();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [filters, setFilters] = useState({
        orderId: '',
        caseId: '',
        email: '',
        firstName: '',
        lastName: ''
    });
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [tableFilters, setTableFilters] = useState({
        'order_id': { value: null, matchMode: FilterMatchMode.CONTAINS },
        'case_id': { value: null, matchMode: FilterMatchMode.CONTAINS },
        'address_to_name': { value: null, matchMode: FilterMatchMode.CONTAINS },
        'status': { value: null, matchMode: FilterMatchMode.CONTAINS },
        'tracking_id': { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
    const [editDialog, setEditDialog] = useState(false);
    const [editingOrder, setEditingOrder] = useState(null);
    const [editForm, setEditForm] = useState({
        status: '',
        status_details: '',
        tracking_id: '',
        next_delivery_date: null
    });
    const toast = useRef(null);
    const [expandedRows, setExpandedRows] = useState([]);

    const tabConfigs = [
        { label: 'Sent', status: 'sent', icon: 'pi pi-check-circle' },
        { label: 'Due', status: 'due', icon: 'pi pi-calendar' },
        { label: 'Issue Orders', status: 'issue', icon: 'pi pi-exclamation-triangle' }
    ];

    const statusOptions = [
        { label: 'Pending', value: 'pending' },
        { label: 'Sent', value: 'sent' },
        { label: 'Delivered', value: 'delivered' },
        { label: 'In Transit', value: 'in_transit' },
        { label: 'Issue', value: 'issue' },
        { label: 'Returned', value: 'returned' },
        { label: 'Cancelled', value: 'cancelled' }
    ];

    useEffect(() => {
        loadMailOrders();
    }, [activeTab, currentMonth, currentYear]);

    // Add custom CSS to hide filter icons
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            .p-column-filter-row .p-column-filter-menu-button {
                display: none !important;
            }
        `;
        document.head.appendChild(style);
        
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    const loadMailOrders = async () => {
        try {
            setLoading(true);
            const config = tabConfigs[activeTab];
            
            // Set authorization header
            if (user.token !== '' && user.token !== null) {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + user.token;
            }
            
            // Calculate date range based on tab and selected month/year
            let startDate = '';
            let endDate = '';
            
            // Get first and last day of the selected month
            const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
            const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
            
            if (config.status === 'due') {
                // For due today, show orders due in the selected month
                startDate = firstDayOfMonth.toISOString().split('T')[0];
                endDate = lastDayOfMonth.toISOString().split('T')[0];
            } else if (config.status === 'sent') {
                // For sent orders, show orders sent in the selected month
                startDate = firstDayOfMonth.toISOString().split('T')[0];
                endDate = lastDayOfMonth.toISOString().split('T')[0];
            } else if (config.status === 'issue') {
                // For issue orders, show orders with issues in the selected month
                startDate = firstDayOfMonth.toISOString().split('T')[0];
                endDate = lastDayOfMonth.toISOString().split('T')[0];
            }

            const response = await axios.post('/api/ha/getMailOrders', {
                orderId: filters.orderId,
                caseId: filters.caseId,
                email: filters.email,
                firstName: filters.firstName,
                lastName: filters.lastName,
                startDate: startDate,
                endDate: endDate,
                status: config.status //=== 'issue' ? 'issue' : ''
            });

            if (response.data.status === 'OK') {
                setData(response.data.data || []);
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.data.errMsg || 'Failed to load mail orders',
                    life: 3000
                });
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.errMsg || 'Failed to load mail orders',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        loadMailOrders();
    };

    const handleClearSearch = () => {
        setFilters({
            orderId: '',
            caseId: '',
            email: '',
            firstName: '',
            lastName: ''
        });
        loadMailOrders();
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleMonthNavigation = (direction) => {
        if (direction === 'forward') {
            if (currentMonth === 11) {
                setCurrentMonth(0);
                setCurrentYear(currentYear + 1);
            } else {
                setCurrentMonth(currentMonth + 1);
            }
        } else {
            if (currentMonth === 0) {
                setCurrentMonth(11);
                setCurrentYear(currentYear - 1);
            } else {
                setCurrentMonth(currentMonth - 1);
            }
        }
    };

    const handleTabChange = (e) => {
        setActiveTab(e.index);
    };

    const caseIdBodyTemplate = (rowData) => {
        return (
            <a 
                href="#" 
                onClick={(e) => {
                    e.preventDefault();
                    navigate(`/CaseDetail/${rowData.case_id}`);
                }}
                className="text-blue-500 hover:text-blue-700 underline"
            >
                {rowData.case_id}
            </a>
        );
    };

    const statusBodyTemplate = (rowData) => {
        const statusColors = {
            'sent': 'bg-success',
            'due_today': 'bg-warning',
            'issue': 'bg-danger',
            'pending': 'bg-info'
        };
        
        return (
            <span className={`badge ${statusColors[rowData.status?.toLowerCase()] || 'bg-secondary'}`}>
                {rowData.status || 'Unknown'}
            </span>
        );
    };

    const dateBodyTemplate = (rowData) => {
        if (!rowData.next_delivery_date) return 'N/A';
        return new Date(rowData.next_delivery_date).toLocaleDateString();
    };

    const createdBodyTemplate = (rowData) => {
        if (!rowData.created) return 'N/A';
        return new Date(rowData.created).toLocaleDateString();
    };

    const updatedBodyTemplate = (rowData) => {
        if (!rowData.updated) return 'N/A';
        return new Date(rowData.updated).toLocaleDateString();
    };

    const actionBodyTemplate = (rowData) => {
        const showTransferButton = activeTab === 1 || activeTab === 2; // due (index 1) or issue (index 2) tabs
        
        return (
            <div className="flex gap-1">
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-success p-button-sm"
                    onClick={() => handleEdit(rowData)}
                    tooltip="Edit Order"
                />
                {showTransferButton && (
                    <Button
                        icon="pi pi-send"
                        className="p-button-rounded p-button-warning p-button-sm"
                        onClick={() => handleTransfer(rowData)}
                        tooltip="Transfer to Honeybee"
                    />
                )}
            </div>
        );
    };

    const handleTransfer = async (order) => {
        try {
            if (user.token !== '' && user.token !== null) {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + user.token;
            }

            const response = await axios.post('/api/ha/TransferOrderToHoneyBee', {
                caseId: order.case_id
            });

            if (response.data.status === 'OK') {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: response.data.data || 'Order transferred to Honeybee successfully',
                    life: 3000
                });
                loadMailOrders(); // Refresh the data
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.data.errMsg || 'Failed to transfer order',
                    life: 3000
                });
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.errMsg || 'Failed to transfer order',
                life: 3000
            });
        }
    };

    const expandedRowTemplate = (data) => {
        const formatJson = (jsonString) => {
            if (!jsonString) return 'N/A';
            try {
                return JSON.stringify(JSON.parse(jsonString), null, 2);
            } catch (e) {
                return jsonString; // Return as-is if not valid JSON
            }
        };

        return (
            <div className="p-3">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <h5 className="mb-3">Patient Information</h5>
                        <div className="space-y-2">
                            <div><strong>First Name:</strong> {data.first_name || 'N/A'}</div>
                            <div><strong>Last Name:</strong> {data.last_name || 'N/A'}</div>
                            <div><strong>Member ID:</strong> {data.member_id || 'N/A'}</div>
                            <div><strong>Carrier:</strong> {data.carrier || 'N/A'}</div>
                            <div><strong>Sent to Curexa:</strong> {data.sent_to_curexa ? 'Yes' : 'No'}</div>
                        </div>
                        <br />
                        <h5 className="mb-3">Address Information</h5>
                        <div className="space-y-2">
                            <div><strong>Street 1:</strong> {data.address_to_street1 || 'N/A'}</div>
                            <div><strong>Street 2:</strong> {data.address_to_street2 || 'N/A'}</div>
                            <div><strong>City:</strong> {data.address_to_city || 'N/A'}</div>
                            <div><strong>State:</strong> {data.address_to_state || 'N/A'}</div>
                            <div><strong>Zip:</strong> {data.address_to_zip || 'N/A'}</div>
                            <div><strong>Country:</strong> {data.address_to_country || 'N/A'}</div>
                            <div><strong>Phone:</strong> {data.address_to_phone || 'N/A'}</div>
                        </div>
                    </div>
                   
                   
                    <div className="col-span-2">
                        <h5 className="mb-3">Curexa Request</h5>
                        <div className="bg-gray-100 p-3 rounded border">
                            <pre className="text-sm whitespace-pre-wrap overflow-x-auto">
                                {formatJson(data.curexa_request)}
                            </pre>
                        </div>
                    </div>
                    <div className="col-span-2">
                        <h5 className="mb-3">Curexa Response</h5>
                        <div className="bg-gray-100 p-3 rounded border">
                            <pre className="text-sm whitespace-pre-wrap overflow-x-auto">
                                {formatJson(data.curexa_response)}
                            </pre>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const handleEdit = (order) => {
        setEditingOrder(order);
        setEditForm({
            status: order.status || '',
            status_details: order.status_details || '',
            tracking_id: order.tracking_id || '',
            next_delivery_date: order.next_delivery_date ? new Date(order.next_delivery_date) : null
        });
        setEditDialog(true);
    };

    const handleSaveEdit = async () => {
        try {
            if (user.token !== '' && user.token !== null) {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + user.token;
            }

            const response = await axios.post('/api/ha/updateMailOrderStatus', {
                order_id: editingOrder.order_id,
                status: editForm.status,
                status_details: editForm.status_details,
                tracking_id: editForm.tracking_id,
                next_delivery_date: editForm.next_delivery_date ? editForm.next_delivery_date.toISOString().split('T')[0] : null
            });

            if (response.data.status === 'OK') {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Order updated successfully',
                    life: 3000
                });
                setEditDialog(false);
                loadMailOrders(); // Refresh the data
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.data.errMsg || 'Failed to update order',
                    life: 3000
                });
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.errMsg || 'Failed to update order',
                life: 3000
            });
        }
    };

    const renderHeader = () => {
        const monthNames = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        
        return (
            <div className="flex justify-content-between align-items-center">
                <div>
                    <h3 className="m-0">Mail Orders - {tabConfigs[activeTab].label}</h3>
                    <p className="mt-1 text-sm text-gray-500">Total Records: {data.length}</p>
                </div>
                <div className="flex align-items-center gap-2">
                    <Button 
                        icon="pi pi-chevron-left" 
                        onClick={() => handleMonthNavigation('backward')}
                        className="p-button-outlined"
                    />
                    <div className="text-center px-3">
                        <h4 className="m-0">{monthNames[currentMonth]} {currentYear}</h4>
                    </div>
                    <Button 
                        icon="pi pi-chevron-right" 
                        onClick={() => handleMonthNavigation('forward')}
                        className="p-button-outlined"
                    />
                </div>
            </div>
        );
    };

    const renderSearchBar = () => {
        return (
            <div className="mb-3">
                <div className="flex flex-row gap-2 align-items-end">
                    <div className="flex-1">
                        <label htmlFor="orderId" className="block mb-1">Order ID</label>
                        <InputText
                            id="orderId"
                            value={filters.orderId}
                            onChange={(e) => handleFilterChange('orderId', e.target.value)}
                            placeholder="Search by Order ID"
                            className="p-inputtext-sm w-full"
                        />
                    </div>
                    <div className="flex-1">
                        <label htmlFor="caseId" className="block mb-1">Case ID</label>
                        <InputText
                            id="caseId"
                            value={filters.caseId}
                            onChange={(e) => handleFilterChange('caseId', e.target.value)}
                            placeholder="Search by Case ID"
                            className="p-inputtext-sm w-full"
                        />
                    </div>
                    <div className="flex-1">
                        <label htmlFor="email" className="block mb-1">Email</label>
                        <InputText
                            id="email"
                            value={filters.email}
                            onChange={(e) => handleFilterChange('email', e.target.value)}
                            placeholder="Search by Email"
                            className="p-inputtext-sm w-full"
                        />
                    </div>
                    <div className="flex-1">
                        <label htmlFor="firstName" className="block mb-1">First Name</label>
                        <InputText
                            id="firstName"
                            value={filters.firstName}
                            onChange={(e) => handleFilterChange('firstName', e.target.value)}
                            placeholder="Search by First Name"
                            className="p-inputtext-sm w-full"
                        />
                    </div>
                    <div className="flex-1">
                        <label htmlFor="lastName" className="block mb-1">Last Name</label>
                        <InputText
                            id="lastName"
                            value={filters.lastName}
                            onChange={(e) => handleFilterChange('lastName', e.target.value)}
                            placeholder="Search by Last Name"
                            className="p-inputtext-sm w-full"
                        />
                    </div>
                    <div className="flex gap-2 ml-2">
                        <Button 
                            label="Search" 
                            icon="pi pi-search" 
                            onClick={handleSearch}
                            className="p-button-primary p-button-sm"
                        />
                        <Button 
                            label="Clear" 
                            icon="pi pi-times" 
                            onClick={handleClearSearch}
                            className="p-button-outlined p-button-sm"
                        />
                    </div>
                </div>
            </div>
        );
    };

    const header = renderHeader();

    return (
        <div className="mt-2">
            <Toast ref={toast} />
            <Card>
                {renderSearchBar()}
                
                <TabView activeIndex={activeTab} onTabChange={handleTabChange}>
                    {tabConfigs.map((config, index) => (
                        <TabPanel key={index} header={config.label} leftIcon={config.icon}>
                            <DataTable 
                                value={data} 
                                paginator 
                                rows={25} 
                                rowsPerPageOptions={[25, 50, 100]} 
                                dataKey="order_id"
                                filters={tableFilters}
                                filterDisplay="row"
                                header={header}
                                emptyMessage="No mail orders found."
                                responsiveLayout="scroll"
                                loading={loading}
                                loadingIcon="pi pi-spinner"
                                className="p-datatable-sm"
                                expandedRows={expandedRows}
                                onRowToggle={(e) => setExpandedRows(e.data)}
                                rowExpansionTemplate={expandedRowTemplate}
                            >
                                <Column expander style={{ width: '3rem' }} />
                                <Column field="order_id" header="Order ID" sortable filter filterPlaceholder="Search by Order ID" showFilterMatchModes={false} />
                                <Column field="case_id" header="Case ID" body={caseIdBodyTemplate} sortable filter filterPlaceholder="Search by Case ID" showFilterMatchModes={false} />
                                <Column field="address_to_name" header="Recipient Name" sortable filter filterPlaceholder="Search by Name" showFilterMatchModes={false} />
                                <Column field="email" header="Email" sortable filter filterPlaceholder="Search by Email" showFilterMatchModes={false} />
                                <Column field="patient_id" header="Patient ID" sortable filter filterPlaceholder="Search by Patient ID" showFilterMatchModes={false} />
                                <Column field="status" header="Status" body={statusBodyTemplate} sortable filter filterPlaceholder="Search by Status" showFilterMatchModes={false} />
                                <Column field="status_details" header="Status Details" sortable filter filterPlaceholder="Search by Status Details" showFilterMatchModes={false} />
                                <Column field="tracking_id" header="Tracking ID" sortable filter filterPlaceholder="Search by Tracking ID" showFilterMatchModes={false} />
                                <Column field="next_delivery_date" header="Next Delivery" body={dateBodyTemplate} sortable />
                                <Column field="created" header="Created" body={createdBodyTemplate} sortable />
                                <Column field="updated" header="Updated" body={updatedBodyTemplate} sortable />
                                <Column body={actionBodyTemplate} header="Actions" style={{ width: '80px' }} />
                            </DataTable>
                        </TabPanel>
                    ))}
                </TabView>
            </Card>
            <Dialog
                visible={editDialog}
                onHide={() => setEditDialog(false)}
                header="Edit Order"
                className="p-dialog-sm"
            >
                <div className="p-fluid p-formgrid p-grid">
                    <div className="p-field p-col-12">
                        <label htmlFor="status">Status</label>
                        <Dropdown
                            id="status"
                            value={editForm.status}
                            options={statusOptions}
                            onChange={(e) => setEditForm({ ...editForm, status: e.value })}
                            placeholder="Select Status"
                        />
                    </div>
                    <div className="p-field p-col-12">
                        <label htmlFor="status_details">Status Details</label>
                        <InputText
                            id="status_details"
                            value={editForm.status_details}
                            onChange={(e) => setEditForm({ ...editForm, status_details: e.target.value })}
                        />
                    </div>
                    <div className="p-field p-col-12">
                        <label htmlFor="tracking_id">Tracking ID</label>
                        <InputText
                            id="tracking_id"
                            value={editForm.tracking_id}
                            onChange={(e) => setEditForm({ ...editForm, tracking_id: e.target.value })}
                        />
                    </div>
                    <div className="p-field p-col-12">
                        <label htmlFor="next_delivery_date">Next Delivery Date</label>
                        <Calendar
                            id="next_delivery_date"
                            value={editForm.next_delivery_date}
                            onChange={(e) => setEditForm({ ...editForm, next_delivery_date: e.value })}
                            dateFormat="mm/dd/yy"
                            showIcon
                        />
                    </div>
                </div>
                <div className="p-dialog-footer">
                    <Button
                        label="Save"
                        icon="pi pi-check"
                        onClick={handleSaveEdit}
                        className="p-button-primary"
                    />
                    <Button
                        label="Cancel"
                        icon="pi pi-times"
                        onClick={() => setEditDialog(false)}
                        className="p-button-outlined"
                    />
                </div>
            </Dialog>
        </div>
    );
}

export default MailOrder; 